import React from 'react';
import { Activity, Radio, AlertTriangle, CheckCircle2, Minus, ShieldCheck, Globe, CalendarClock } from 'lucide-react';
import { cn, coerceToText } from '../../lib/utils';
import ReactMarkdown from 'react-markdown';
import type { PulseOutput, AnalystIntelligenceSummary } from '../../types/geminiTypes';

interface MarketIntelligencePanelProps {
  pulse?: PulseOutput;
  intelligence?: AnalystIntelligenceSummary;
  // 'card' = standalone rounded card (Overview); 'memo' = pdf-section block (Memo/PDF)
  variant?: 'card' | 'memo';
}

// Signal → visual mapping. Strengthening = green, Weakening = red, Stable/other = gray.
function signalStyle(signal?: string) {
  switch (signal) {
    case 'STRENGTHENING':
      return { box: 'bg-success-50 border-success-100', text: 'text-success-700', Icon: CheckCircle2, label: 'Strengthening' };
    case 'WEAKENING':
      return { box: 'bg-danger-50 border-danger-100', text: 'text-danger-700', Icon: AlertTriangle, label: 'Weakening' };
    case 'STABLE':
      return { box: 'bg-surface-2 border-ink-200', text: 'text-ink-600', Icon: Minus, label: 'Stable' };
    default:
      return { box: 'bg-surface-2 border-ink-200', text: 'text-ink-500', Icon: Minus, label: 'Not assessed' };
  }
}

export function MarketIntelligencePanel({ pulse, intelligence, variant = 'card' }: MarketIntelligencePanelProps) {
  // Nothing to show (e.g. legacy saved memo with neither payload)
  if (!pulse && !intelligence) return null;

  const wrapper =
    variant === 'card'
      ? 'oi-card p-6 md:p-8'
      : 'oi-card mb-10 p-6 md:p-7';

  const showPulse = !!pulse;
  const pulseInScope = pulse?.scopeStatus === 'IN_SCOPE';
  const s = signalStyle(pulse?.culturalSignal);

  const conf = intelligence?.searchConfidenceScore ?? 0;
  const confLabel = conf >= 0.8 ? 'High' : conf >= 0.5 ? 'Moderate' : 'Limited';
  const confColor = conf >= 0.8 ? 'text-success-600' : conf >= 0.5 ? 'text-ink-500' : 'text-warning-600';

  return (
    <div className={wrapper}>
      <h5 className="font-medium text-ink uppercase text-[10px] tracking-[0.3em] mb-5 flex items-center gap-3">
        <Activity className="w-4 h-4" />
        Market Intelligence
        <span className="ml-auto text-[9px] font-medium text-ink-500 normal-case tracking-normal">
          Live web-grounded · informational
        </span>
      </h5>

      {/* ── BRAND PULSE (Agent F) ───────────────────────────────── */}
      {showPulse && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Radio className="w-3.5 h-3.5 text-ink-500" />
            <span className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Brand Pulse</span>
          </div>

          {!pulseInScope ? (
            <div className="p-4 bg-surface-2 border border-ink-200 rounded-md">
              <p className="text-xs text-ink-500 leading-relaxed">
                Brand Pulse not assessed — this company's moat is not primarily brand or
                customer-relationship driven, so the brand-signal agent was intentionally skipped.
              </p>
            </div>
          ) : (
            <div className={cn('p-4 rounded-md border', s.box)}>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className={cn('inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-widest', s.text)}>
                  <s.Icon className="w-3.5 h-3.5" /> Customer relationship: {s.label}
                </span>
                {pulse?.directionalConfidence && pulse.directionalConfidence !== 'NOT_ASSESSED' && (
                  <span className="text-[9px] font-medium uppercase tracking-wider text-ink-500 bg-surface/60 px-2 py-0.5 rounded-md">
                    {pulse.directionalConfidence} confidence
                  </span>
                )}
                {pulse?.managementAwareness && pulse.managementAwareness !== 'NOT_ASSESSED' && (
                  <span className="text-[9px] font-medium uppercase tracking-wider text-ink-500 bg-surface/60 px-2 py-0.5 rounded-md">
                    Mgmt: {pulse.managementAwareness.replace(/_/g, ' ').toLowerCase()}
                  </span>
                )}
              </div>
              {pulse?.brandMoatImpact && (
                <p className="text-sm text-ink-600 leading-relaxed mb-3">{coerceToText(pulse.brandMoatImpact)}</p>
              )}
              {Array.isArray(pulse?.keyEvidence) && pulse.keyEvidence.length > 0 && (
                <ul className="space-y-1.5">
                  {pulse.keyEvidence.map((e, i) => (
                    <li key={i} className="text-xs text-ink-600 leading-relaxed flex gap-2">
                      <span className="text-ink-300 mt-0.5">•</span><span>{coerceToText(e)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── ANALYST INTELLIGENCE (Agent D) ──────────────────────── */}
      {intelligence && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-3.5 h-3.5 text-ink-500" />
            <span className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Current Intelligence</span>
            <span className={cn('ml-auto text-[9px] font-medium uppercase tracking-wider', confColor)}>
              {confLabel} source confidence
            </span>
          </div>

          {intelligence.keyContextSummary && (
            <div className="text-sm text-ink-600 leading-relaxed mb-4 prose prose-sm max-w-none">
              <ReactMarkdown>{coerceToText(intelligence.keyContextSummary)}</ReactMarkdown>
            </div>
          )}

          {Array.isArray(intelligence.upcomingCatalysts) && intelligence.upcomingCatalysts.length > 0 && (
            <div className="mb-4">
              <p className="text-[9px] font-medium uppercase tracking-widest text-ink-500 mb-2 flex items-center gap-1.5">
                <CalendarClock className="w-3 h-3" /> Upcoming catalysts
              </p>
              <div className="flex flex-wrap gap-2">
                {intelligence.upcomingCatalysts.map((c, i) => (
                  <span key={i} className="text-xs text-ink-600 bg-surface-2 border border-ink-200 px-3 py-1 rounded-md">{coerceToText(c)}</span>
                ))}
              </div>
            </div>
          )}

          {/* Sources + EDGAR badge — the core trust signal */}
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-ink-200">
            {intelligence.edgarFilingsUsed && (
              <span className="inline-flex items-center gap-1.5 text-[9px] font-medium uppercase tracking-wider text-success-700 bg-success-50 border border-success-100 px-2.5 py-1 rounded-md">
                <ShieldCheck className="w-3 h-3" /> SEC filings used
              </span>
            )}
            {Array.isArray(intelligence.sourcesConsulted) && intelligence.sourcesConsulted.slice(0, 8).map((src, i) => (
              <span key={i} className="text-[9px] font-medium text-ink-500 bg-surface-2 border border-ink-200 px-2.5 py-1 rounded-md">{src}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
